import React, { useState, useEffect } from "react"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { gtmTracking } from "@/utils/gtm-tracking"

export const FeatureDemoTab = ({
  kicker,
  title,
  description,
  image,
  imageAlt,
  alignAsset = "left",
  id,
  "data-active": dataActive,
  ...rest
}) => {
  return (
    <div
      className="feature-tabs__content"
      id={id}
      data-active={dataActive}
      {...rest}
    >
      <div
        className={`feature-tabs__content-inner ${alignAsset === "right" ? "feature-tabs__content-inner--right" : ""}`}
      >
        <div className="feature-tabs__content-inner-text">
          {kicker && <span className="heading-kicker">{kicker}</span>}
          {title && <h2 className="feature-tabs__title">{title}</h2>}
        </div>

        {description && (
          <div className="feature-tabs__description p--lg">{description}</div>
        )}
      </div>
      {image && (
        <div className="feature-tabs__image">
          <GatsbyImage
            image={getImage(image.src)}
            alt={imageAlt || title || ""}
          />
        </div>
      )}
    </div>
  )
}

const FeatureDemoTabsHeader = ({ children }) => {
  const [activeTab, setActiveTab] = useState(
    children.find(child => child.props.isDefault)?.props?.name ??
      children[0].props.name,
  )

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace("#", "")
      if (!hash) return

      const targetTab = children.find(child => child.props.id === hash)
      if (!targetTab) return

      setActiveTab(targetTab.props.name)

      // Delay smooth scroll to override gatsby-browser.js's instant scroll
      // gatsby-browser.js uses setTimeout(..., 0), so we use a slightly longer delay
      setTimeout(() => {
        const element = document.getElementById(hash)
        if (!element) return

        const yOffset = -200
        const y = element.getBoundingClientRect().top + window.scrollY + yOffset

        window.scrollTo({ top: y, behavior: "smooth" })
      }, 50)
    }

    handleHashChange()
    window.addEventListener("hashchange", handleHashChange)
    return () => window.removeEventListener("hashchange", handleHashChange)
  }, [children])

  const tabCount = React.Children.count(children)

  return (
    <section className="container-fluid">
      {/* Tabs navigation */}
      <div
        className="feature-tabs__nav"
        style={{ "--feature-tabs-count": tabCount }}
      >
        {children.map(child => (
          <button
            key={child.props.name}
            type="button"
            className="mb-0 feature-tabs__tab h5"
            data-active={activeTab === child.props.name ? "true" : "false"}
            onClick={() => {
              gtmTracking(
                "Tab Click",
                child.props.name,
                "Feature Demo Tabs Header",
              )()
              setActiveTab(child.props.name)
            }}
          >
            {child.props.name}
          </button>
        ))}
      </div>

      {/* Desktop: Content container (only active tab) */}
      <div className="feature-tabs__content-container">
        {children.map(child => {
          const isActive = activeTab === child.props.name
          return React.cloneElement(child, {
            key: child.props.name,
            "data-active": isActive ? "true" : "false",
          })
        })}
      </div>

      {/* Mobile: Each tab followed by its content */}
      <div className="feature-tabs__mobile-container">
        {children.map(child => (
          <div key={child.props.name} className="feature-tabs__mobile-item">
            {child}
          </div>
        ))}
      </div>
    </section>
  )
}

export default FeatureDemoTabsHeader
